import { cx, isAIAssistantApiError, useTranslations } from "@jugl-web/utils";
import React from "react";
import Markdown from "markdown-to-jsx";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { PlainButton } from "@jugl-web/ui-components/cross-platform/PlainButton";
import { useAIAssistant } from "@jugl-web/domain-resources/ai-assistant/providers/useAIAssistant";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";
import { ReactComponent as CheckIcon } from "./assets/check.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as UserPerformance } from "./assets/user-performance.svg";
import { AILoadingTile } from "./components/AILoadingTile";
import { AIAssistantLimitAlert } from "../AIAssistantLimitAlert";
import { aiGradientBackgroundClassName } from "../consts";
import { AIErrorState } from "../AIErrorState";

export const AIUserPerformanceDialog: React.FC<{
  entityId: string;
  userId: string;
  onClose: () => void;
}> = ({ entityId, userId, onClose }) => {
  const { t } = useTranslations();
  const { copyData, isDataCopied } = useAIAssistant();
  const { aiAssistantApi } = useRestApiProvider();

  const { data, error, isFetching, isError } =
    aiAssistantApi.useGetUserPerformanceQuery({
      entityId,
      userId,
    });

  return (
    <div className="fixed right-5 bottom-5 z-[50] flex h-[940px] max-h-[90vh] w-[560px] max-w-[90vh] animate-fade-in flex-col overflow-hidden rounded-[20px] bg-white shadow-[0_24px_56px_rgba(0,0,0,0.32)]">
      <div className="flex items-center justify-between border-0 border-b border-solid border-[#ECEFF0] bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <div
            className={cx(
              "flex h-11 w-11 items-center justify-center rounded-full border border-solid border-grey-200",
              aiGradientBackgroundClassName
            )}
          >
            <UserPerformance />
          </div>
          <span className="font-secondary text-lg font-medium text-[#383838]">
            {t({
              id: "ai-user-performance-dialog-component.user-performance",
              defaultMessage: "User Performance",
            })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {data && !isFetching && !isError && (
            <PlainButton
              className={cx(
                "flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-grey-200",
                isDataCopied && "bg-[#E8E0F4] hover:bg-[#E8E0F4]"
              )}
              disabled={isDataCopied}
              onClick={() => copyData(data)}
            >
              {isDataCopied ? <CheckIcon /> : <CopyIcon />}
            </PlainButton>
          )}
          <PlainButton
            className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-grey-200"
            onClick={onClose}
          >
            <CloseIcon />
          </PlainButton>
        </div>
      </div>
      <div className="jugl__custom-scrollbar h-full w-full overflow-y-auto px-6 py-6">
        {(() => {
          if (isFetching) {
            return <AILoadingTile />;
          }

          if (
            error &&
            isAIAssistantApiError(error) &&
            error.data.message.error === "quota_limit_exceeded"
          ) {
            return (
              <div className="flex h-full items-center justify-center">
                <AIAssistantLimitAlert orientation="column" />
              </div>
            );
          }

          if (isError) {
            return (
              <div className="flex h-full items-center justify-center">
                <AIErrorState onRetry={() => {}} orientation="column" />
              </div>
            );
          }

          if (data) {
            return (
              <Markdown
                className="jugl__break-word font-secondary text-sm whitespace-pre-wrap"
                options={{
                  overrides: {
                    h1: <span className="mt-1 mb-3 text-lg" />,
                    h2: <span className="my-2.5 text-[15px]" />,
                  },
                }}
              >
                {data || ""}
              </Markdown>
            );
          }

          return null;
        })()}
      </div>
    </div>
  );
};
